/**
 * Regras PURAS da instrumentacao de comportamento (GTM + Microsoft Clarity).
 *
 * Tudo aqui e deterministico e sem efeito colateral, para ser testado em Node
 * (ver __analytics.test.js). O lado que mexe em `window`/DOM fica em `index.ts`.
 *
 * Principios:
 *  - so as superficies PUBLICAS do personalizador sao instrumentadas; painel,
 *    editor, pedidos, laboratorios e login nunca carregam o GTM;
 *  - nada de dado pessoal: nenhum texto digitado, nome, e-mail, CPF, telefone,
 *    endereco, URL de imagem enviada ou parametro de consulta sai daqui. Os
 *    campos de evento passam por uma lista FECHADA (`ALLOWED_PROPS`);
 *  - a ausencia/bloqueio do GTM nunca afeta o personalizador.
 */

/** Conteiner Web exclusivo do Turbine. Sobrescrevivel por VITE_TURBINE_GTM_ID. */
export const DEFAULT_GTM_ID = 'GTM-T5JRR5RC';

/** Rotas publicas instrumentadas (React Router). */
const TRACKABLE_PATHS = [
  /^\/customize\/[^/]+\/?$/,
  /^\/customize-catalog\/[^/]+\/?$/,
  /^\/customize-group\/[^/]+\/?$/,
];

export function isTrackablePath(pathname: string): boolean {
  return TRACKABLE_PATHS.some((re) => re.test(pathname || ''));
}

export type AnalyticsSurface = 'customizer' | 'gallery3d' | 'catalog' | 'group' | 'other';
export type AnalyticsEntry = 'shopify_embed' | 'turbine_nested' | 'direct';

export interface AnalyticsEnvironmentInput {
  pathname: string;
  search: string;
  hostname: string;
  /** navigator.webdriver: robos (Store Monitor, Playwright) nao viram gravacao. */
  webdriver: boolean;
  /** Libera a coleta em URL com parametros corporativos (decisao explicita). */
  allowCorporate?: boolean;
  /** Valor de VITE_TURBINE_ANALYTICS ('off' desliga tudo). */
  killSwitch?: string | null;
  gtmIdFromEnv?: string | null;
}

export interface AnalyticsDecision {
  enabled: boolean;
  reason: string;
  gtmId: string;
}

const GTM_ID_RE = /^GTM-[A-Z0-9]{4,12}$/;

/**
 * Decide se este DOCUMENTO carrega o GTM. Uma decisao por documento: o GTM nao
 * pode ser descarregado depois, entao a protecao de transicoes SPA para rotas
 * nao publicas e feita em `index.ts` (o Clarity e parado).
 */
export function decideAnalytics(input: AnalyticsEnvironmentInput): AnalyticsDecision {
  const envId = String(input.gtmIdFromEnv || '').trim();
  const gtmId = GTM_ID_RE.test(envId) ? envId : DEFAULT_GTM_ID;
  const off = (reason: string): AnalyticsDecision => ({ enabled: false, reason, gtmId });
  if (String(input.killSwitch || '').trim().toLowerCase() === 'off') return off('desligado-por-configuracao');
  if (!isTrackablePath(input.pathname)) return off('rota-nao-publica');
  if (input.webdriver) return off('automacao');
  // Contexto corporativo (Resgate): companyId/companyName/corporateLogoUrl iriam na URL gravada.
  if (/[?&](companyId|companyName|corporateLogoUrl)=/.test(input.search || '') && !input.allowCorporate) return off('contexto-corporativo');
  let sp: URLSearchParams;
  try { sp = new URLSearchParams(input.search || ''); } catch { return off('url-invalida'); }
  // Modo render: o servidor abre o personalizador em Chromium headless para a
  // API de preview. Nao e cliente navegando.
  if (sp.get('render') === '1') return off('modo-render');
  return { enabled: true, reason: 'ok', gtmId };
}

/**
 * Quando o GTM entra:
 *  - 'settled'     depois do marco `scene-settled` (cena assentada);
 *  - 'interactive' depois de `viewer-interactive`;
 *  - 'load'        depois do `load` do documento, em idle (catalogos, sem cena 3D).
 * O personalizador e a galeria sao pesados (payload, GLB, texturas, WebGL): o GTM
 * e o Clarity nao podem disputar banda e CPU com eles, entao esperam a cena.
 */
export type GtmLoadMode = 'settled' | 'interactive' | 'load';

/** Marco de performance que libera o GTM em cada modo. */
export const GTM_MODE_MARK: Record<Exclude<GtmLoadMode, 'load'>, string> = {
  settled: 'scene-settled',
  interactive: 'viewer-interactive',
};

/** Se o marco nunca ocorrer (erro, produto sem modelo), o GTM entra neste prazo. */
export const GTM_FALLBACK_MS = 15_000;

/** Eventos guardados antes do GTM existir (limite do buffer; o excedente e descartado). */
export const MAX_BUFFERED_EVENTS = 40;

/**
 * O GTM/Clarity so carregam depois que a loja CONCEDE analytics (padrao). Funcionar
 * "sem cookies" nao e autorizacao: o Clarity ainda envia estrutura da pagina e
 * interacoes. VITE_TURBINE_ANALYTICS_CONSENT=optional (so se o juridico decidir)
 * permite carregar em modo sem consentimento.
 */
export function consentRequired(envValue: string | null | undefined): boolean {
  return String(envValue || '').trim().toLowerCase() !== 'optional';
}

export function defaultGtmLoadMode(surface: AnalyticsSurface): GtmLoadMode {
  return surface === 'customizer' || surface === 'gallery3d' ? 'settled' : 'load';
}

/**
 * Selecao de variante para MEDICAO (?tb_an=off|load|interactive|settled). So vale
 * fora de producao (sandbox/localhost): em producao o parametro e ignorado.
 */
export function resolveGtmLoadMode(
  surface: AnalyticsSurface,
  search: string,
  env: 'production' | 'sandbox' | 'development',
): { mode: GtmLoadMode; off: boolean } {
  const fallback = { mode: defaultGtmLoadMode(surface), off: false };
  if (env === 'production') return fallback;
  let v = '';
  try { v = String(new URLSearchParams(search).get('tb_an') || '').toLowerCase(); } catch { return fallback; }
  if (v === 'off') return { mode: fallback.mode, off: true };
  if (v === 'load' || v === 'interactive' || v === 'settled') return { mode: v, off: false };
  return fallback;
}

export function surfaceFor(pathname: string, search: string): AnalyticsSurface {
  if (/^\/customize-catalog\//.test(pathname)) return 'catalog';
  if (/^\/customize-group\//.test(pathname)) return 'group';
  if (/^\/customize\//.test(pathname)) {
    try {
      if (new URLSearchParams(search).get('mediaOnly') === 'true') return 'gallery3d';
    } catch { /* noop */ }
    return 'customizer';
  }
  return 'other';
}

export function environmentFor(hostname: string, search: string): 'production' | 'sandbox' | 'development' {
  if (/^(localhost|127\.0\.0\.1)$/.test(hostname)) return 'development';
  if (/(^|\.)sandbox\./.test(hostname)) return 'sandbox';
  try {
    if (new URLSearchParams(search).get('turbine-sandbox') === '1') return 'sandbox';
  } catch { /* noop */ }
  return 'production';
}

/**
 * De onde o documento foi aberto. `turbine_nested` = iframe dentro do proprio
 * Turbine (catalogo/grupo abrindo o personalizador): sao DOIS documentos e,
 * portanto, duas sessoes no Clarity, correlacionadas pela tag turbine_session.
 */
export function entryFor(embedded: boolean, parentIsTurbine: boolean): AnalyticsEntry {
  if (!embedded) return 'direct';
  return parentIsTurbine ? 'turbine_nested' : 'shopify_embed';
}

export function deviceFor(width: number, coarsePointer: boolean): 'mobile' | 'tablet' | 'desktop' {
  if (coarsePointer && width < 768) return 'mobile';
  if (coarsePointer && width < 1200) return 'tablet';
  if (width < 600) return 'mobile';
  return 'desktop';
}

// ── Eventos ────────────────────────────────────────────────────────────────

/** Eventos de uma vez por documento (protege contra rerender/StrictMode). */
export const ONCE_PER_DOCUMENT = new Set([
  'customizer_opened',
  'product_loaded',
  'viewer_ready',
  'personalization_started',
]);

/**
 * A primeira ocorrencia por documento e a selecao AUTOMATICA ao abrir (cor/variante
 * da URL ou padrao), nao uma troca feita pelo cliente: nao vira evento.
 */
export const SKIP_FIRST_PER_DOCUMENT = new Set(['color_changed', 'variant_changed']);

/** Unicos campos que podem sair num evento. Valores: string curta, numero ou booleano. */
export const ALLOWED_PROPS = new Set([
  'source',
  'step',
  'option_type',
  'selection',
  'component',
  'category',
  'reason',
  'result',
  'duration_ms',
  'attempt',
  'mime_type',
  'phase',
  'role',
]);

const MAX_STRING = 60;

/** Mantem so campos permitidos e tipos simples; corta strings longas. */
export function sanitizeProps(props: Record<string, unknown> | null | undefined): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!props || typeof props !== 'object') return out;
  for (const [k, v] of Object.entries(props)) {
    if (!ALLOWED_PROPS.has(k)) continue;
    if (typeof v === 'boolean') out[k] = v;
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = Math.round(v);
    else if (typeof v === 'string' && v.trim()) out[k] = v.trim().slice(0, MAX_STRING);
  }
  return out;
}

export interface MappedEvent {
  name: string;
  props: Record<string, string | number | boolean>;
  /** Categoria de erro (vira tag e prioriza a gravacao no Clarity). */
  errorCategory?: string;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

/**
 * Traduz um evento do `trackEvent` interno do personalizador (que ja dispara
 * apenas em transicoes reais) para o vocabulario de analytics. Retorna null
 * para eventos sem equivalente. Nunca copia campos livres do evento original.
 */
export function mapTurbineEvent(eventType: string, data: Record<string, unknown> = {}): MappedEvent | null {
  switch (eventType) {
    case 'customizer_open':
      return { name: 'customizer_opened', props: sanitizeProps({ source: str(data.source) || 'direct' }) };
    case 'customizer_close':
      return { name: 'customizer_closed', props: {} };
    case 'product_loaded':
      return { name: 'product_loaded', props: {} };
    case 'color_select':
      return { name: 'color_changed', props: {} };
    case 'variant_axis_select':
      return { name: 'variant_changed', props: {} };
    case 'personalization_choice':
      return data.wants === true
        ? { name: 'personalization_started', props: {} }
        : { name: 'personalization_declined', props: {} };
    case 'step_view':
      return { name: 'personalization_step_changed', props: sanitizeProps({ step: str(data.step) }) };
    case 'style_select':
      return { name: 'personalization_option_selected', props: sanitizeProps({ selection: 'style', option_type: str(data.styleType) }) };
    case 'font_select':
      return { name: 'personalization_option_selected', props: { selection: 'font' } };
    case 'icon_select':
      return { name: 'personalization_option_selected', props: { selection: 'icon' } };
    case 'orientation_select':
      return { name: 'personalization_option_selected', props: { selection: 'orientation' } };
    case 'add_to_cart':
      return { name: 'add_to_cart_clicked', props: {} };
    case 'customizer_ar_click':
    case 'gallery_3d_ar_click':
      return { name: 'ar_clicked', props: {} };
    case 'preview_capture': {
      const ok = data.resultado === 'ok';
      return ok
        ? { name: 'preview_generated', props: {} }
        : { name: 'customizer_error', props: { category: 'preview' }, errorCategory: 'preview' };
    }
    case 'viewer_error':
      return { name: 'customizer_error', props: { category: 'viewer' }, errorCategory: 'viewer' };
    case 'viewer_timeout':
      return { name: 'customizer_error', props: { category: 'slow_load' }, errorCategory: 'slow_load' };
    case 'font_missing_glyph':
      return { name: 'customizer_error', props: { category: 'font_glyph' }, errorCategory: 'font_glyph' };
    case 'catalog_category_view':
      return { name: 'catalog_category_viewed', props: {} };
    case 'catalog_item_click':
      return { name: 'catalog_item_selected', props: {} };
    case 'catalog_close':
      return { name: 'catalog_closed', props: {} };
    default:
      return null;
  }
}

/**
 * Classifica um marcador do Store Monitor (texto livre, pode conter URL ou
 * mensagem) numa categoria FECHADA. So a categoria vai para o Clarity; o
 * detalhe continua indo para o Store Monitor como sempre.
 */
export function errorCategoryForMonitorMarker(marker: string): string {
  const m = String(marker || '').toLowerCase();
  if (m.includes('webgl context lost')) return 'webgl_context_lost';
  if (m.includes('fonte')) return 'font_load';
  if (m.includes('imagem') || m.includes('ícone') || m.includes('icone')) return 'image_load';
  if (m.includes('css') || m.includes('script') || m.includes('recurso')) return 'resource_load';
  if (m.includes('promise')) return 'unhandled_rejection';
  if (m.includes('glb') || m.includes('modelo') || m.includes('model')) return 'model_load';
  if (m.includes('textur')) return 'texture_load';
  return 'js_error';
}

// ── Consentimento ──────────────────────────────────────────────────────────

/**
 * Origens que podem informar o consentimento do visitante ao iframe. So a loja
 * (e o Resgate) conhecem a decisao do visitante (Shopify Customer Privacy API);
 * qualquer outra origem e ignorada.
 */
export const CONSENT_ORIGINS = [
  /^https:\/\/(www\.)?paccoby\.com\.br$/,
  /^https:\/\/resgate\.paccoby\.com\.br$/,
  /^https:\/\/[a-z0-9-]+\.myshopify\.com$/,
];

export function isConsentOrigin(origin: string): boolean {
  return CONSENT_ORIGINS.some((re) => re.test(String(origin || '')));
}

export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
}

/** Mensagem `turbine:consent` estrita: booleanos explicitos, nada mais. */
export function parseConsentMessage(data: unknown): ConsentState | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.type !== 'turbine:consent') return null;
  if (typeof d.analytics !== 'boolean' || typeof d.marketing !== 'boolean') return null;
  return { analytics: d.analytics, marketing: d.marketing };
}
